import api from './api';
import { Order, OrderSearchParams } from '../types';
import axios, { AxiosResponse } from 'axios';
import LogService from './logService';
import AuthService from './authService';

const getAll = () => {
  return api.get('/orders');
};

const getById = (id: number) => {
  return api.get(`/orders/${id}`);
};

const getByCustomer = (customerId: number) => {
  return api.get(`/orders/customer/${customerId}`);
};

const search = (params: OrderSearchParams) => {
  return api.get('/orders/search', { params });
};

// Hàm tạo log
const createLog = async (action: 'created' | 'updated' | 'deleted', order: Order, details?: string) => {
  try {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) return;
    
    const logData = {
      action,
      resourceType: 'order' as 'order' | 'customer' | 'staff',
      resourceId: order.id as number,
      details: details || `Đơn hàng "${order.product}" đã được ${action === 'created' ? 'tạo mới' : action === 'updated' ? 'cập nhật' : 'xóa'}`,
      performedById: currentUser.id as number
    };
    
    // Tạo log
    await LogService.create(logData);
  } catch (error) {
    console.error('Lỗi khi tạo log:', error);
  }
};

const create = async (data: Order, image?: File) => {
  // Log trước khi gửi để debug
  console.log("Creating order data:", JSON.stringify(data));
  
  let response;
  
  if (image) {
    const formData = new FormData();
    
    // Thêm các trường dữ liệu vào FormData
    Object.keys(data).forEach(key => {
      const value = data[key as keyof Order];
      if (key !== 'imagePath' && value !== undefined) {
        // Với trường totalValue, deposit và remainingAmount, đảm bảo chuyển thành số
        if (key === 'totalValue' || key === 'deposit' || key === 'remainingAmount' || key === 'quantity' || key === 'price' || key === 'shippingFee') {
          formData.append(key, String(Number(value)));
        } else {
          formData.append(key, String(value));
        }
      }
    });
    
    // Thêm file vào FormData
    formData.append('image', image);
    
    response = await api.post('/orders', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  } else {
    // Với API không có file, convert dữ liệu số đúng cách
    const processedData: Order = { ...data };
    
    // Convert số
    if (processedData.totalValue !== undefined) processedData.totalValue = Number(processedData.totalValue);
    if (processedData.deposit !== undefined) processedData.deposit = Number(processedData.deposit);
    if (processedData.remainingAmount !== undefined) processedData.remainingAmount = Number(processedData.remainingAmount);
    if (processedData.quantity !== undefined) processedData.quantity = Number(processedData.quantity);
    if (processedData.price !== undefined) processedData.price = Number(processedData.price);
    if (processedData.shippingFee !== undefined) processedData.shippingFee = Number(processedData.shippingFee);
    
    console.log("Processed create data:", processedData);
    response = await api.post('/orders', processedData);
  }
  
  // Tạo log sau khi tạo đơn hàng thành công
  if (response && response.data) {
    await createLog('created', response.data);
  }
  
  return response;
};

const update = async (id: number, data: Order, image?: File) => {
  // Log trước khi gửi để debug
  console.log("Updating order data:", JSON.stringify(data));
  
  let response;
  
  if (image) {
    const formData = new FormData();
    
    // Thêm các trường dữ liệu vào FormData
    Object.keys(data).forEach(key => {
      const value = data[key as keyof Order];
      if (key !== 'imagePath' && value !== undefined) {
        // Với trường totalValue, deposit và remainingAmount, đảm bảo chuyển thành số
        if (key === 'totalValue' || key === 'deposit' || key === 'remainingAmount' || key === 'quantity' || key === 'price' || key === 'shippingFee') {
          formData.append(key, String(Number(value)));
        } else {
          formData.append(key, String(value));
        }
      }
    });
    
    // Thêm file vào FormData
    formData.append('image', image);
    
    response = await api.put(`/orders/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  } else {
    // Với API không có file, convert dữ liệu số đúng cách
    const processedData: Order = { ...data };
    
    // Convert số
    if (processedData.totalValue !== undefined) processedData.totalValue = Number(processedData.totalValue);
    if (processedData.deposit !== undefined) processedData.deposit = Number(processedData.deposit);
    if (processedData.remainingAmount !== undefined) processedData.remainingAmount = Number(processedData.remainingAmount);
    if (processedData.quantity !== undefined) processedData.quantity = Number(processedData.quantity);
    if (processedData.price !== undefined) processedData.price = Number(processedData.price);
    if (processedData.shippingFee !== undefined) processedData.shippingFee = Number(processedData.shippingFee);
    
    console.log("Processed update data:", processedData);
    response = await api.put(`/orders/${id}`, processedData);
  }
  
  // Tạo log sau khi cập nhật đơn hàng thành công
  if (response && response.data) {
    await createLog('updated', response.data);
  }
  
  return response;
};

const remove = async (id: number) => {
  // Lấy thông tin đơn hàng trước khi xóa
  const orderResponse = await getById(id);
  const order = orderResponse.data;
  
  // Xóa đơn hàng
  const response = await api.delete(`/orders/${id}`);
  
  // Tạo log sau khi xóa đơn hàng thành công
  if (order) {
    await createLog('deleted', order);
  }
  
  return response;
};

const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) return null;
  
  console.log('OrderService - Yêu cầu URL hình ảnh từ đường dẫn:', imagePath);
  
  // Đảm bảo apiUrl không có dấu / ở cuối
  const apiUrl = ( 'https://api.hoaithuongfruits.com').replace(/\/$/, '');
  
  // Kiểm tra nếu đường dẫn là /api/images/orders/...
  if (imagePath.includes('/api/images/orders/')) {
    // Trích xuất tên file từ đường dẫn
    const parts = imagePath.split('/');
    const filename = parts[parts.length - 1];
    
    // Tạo URL trực tiếp đến route xử lý hình ảnh
    const directUrl = `${apiUrl}/api/images/orders/${filename}?t=${Date.now()}`;
    console.log('OrderService - URL hình ảnh trực tiếp:', directUrl);
    return directUrl;
  }
  
  // Đảm bảo imagePath có dấu / ở đầu 
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  const fullUrl = `${apiUrl}${normalizedPath}`;
  console.log('OrderService - URL hình ảnh đầy đủ:', fullUrl);
  
  return fullUrl;
};

interface OrderServiceInterface {
  getAll: () => Promise<AxiosResponse<Order[]>>;
  getById: (id: number) => Promise<AxiosResponse<Order>>;
  getByCustomer: (customerId: number) => Promise<AxiosResponse<Order[]>>;
  search: (params: OrderSearchParams) => Promise<AxiosResponse<Order[]>>;
  create: (data: Order, image?: File) => Promise<AxiosResponse<Order>>;
  update: (id: number, data: Order, image?: File) => Promise<AxiosResponse<Order>>;
  delete: (id: number) => Promise<AxiosResponse<void>>;
  getImageUrl: (imagePath: string | null | undefined) => string | null;
}

const OrderService: OrderServiceInterface = {
  getAll,
  getById,
  getByCustomer,
  search,
  create,
  update,
  delete: remove,
  getImageUrl
};

export default OrderService; 